import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { AppDispatch, RootState } from "../../../../redux/store";
import {
  fetchElectricCategories,
  deleteElectricCategory,
} from "../../../../features/auth/store/electricCategorySlice";
import ElectricCategoriesTable from "./ElectricCategoriesTable";
import { CreateElectricCategoryModal } from "../../Modal/Electric/CreateElectricCategoryModal";
import {
  ExternalContainer,
  InternalContainer,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { EditElectricCategoryModal } from "../../Modal/Electric/EditElectricCategoryModal";
import { DeleteElectricCategoryModal } from "../../Modal/Electric/DeleteElectricCategoryModal";

const ElectricCategories: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const { electricCategories, loading, error } = useSelector(
    (state: RootState) => state.electricCategories
  );

  useEffect(() => {
    dispatch(fetchElectricCategories());
  }, [dispatch]);

  const openDeleteModal = async (id: number) => {
    setSelectedCategory(id);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSelectedCategory(id);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await dispatch(deleteElectricCategory(id)).unwrap();
      toast.success("Category deleted!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete category");
    }
  };

  // Filter and search logic
  const filteredCategories = electricCategories.filter((category) => {
    const matchesStatus = !statusFilter || category.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      category.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <ExternalContainer>
      <div className="h-screen dark:bg-[#FFFFFF1A] w-[1030px]">
        <InternalContainer headerText="Electric Categories">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-4 mb-4 w-full">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border p-2 rounded"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="">All Status</option>
                <option value="on">Active</option>
                <option value="off">Inactive</option>
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-[200px] h-10 ml-6"
            >
              New Category
            </Button>
          </div>

          <ElectricCategoriesTable
            categories={filteredCategories}
            loading={loading}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
          />
          <CreateElectricCategoryModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />
          <EditElectricCategoryModal
            isOpen={isEditModalOpen}
            id={selectedCategory}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedCategory(null);
            }}
          />

          <DeleteElectricCategoryModal
            id={selectedCategory}
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedCategory(null);
            }}
          />
        </InternalContainer>
      </div>
    </ExternalContainer>
  );
};

export default ElectricCategories;
